import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import ShortcutButton from './ShortcutButton';
import { useTheme } from '../../middleware/ThemeContext';
import { Theme } from '../../utils/themes';

type DropdownItem = {
    label: string;
    onClick: () => void;
    id?: string | number;
    icon?: React.ReactNode;                // optional icon for menu item
    iconPosition?: 'left' | 'right';       // optional icon position
    selected?: boolean;
};

type DropdownButtonProps = {
    buttonLabel: string;
    items: DropdownItem[];
    position?: 'top' | 'bottom';
    align?: 'left' | 'right';
    icon?: React.ReactNode;                 // icon for toggle button
    iconPosition?: 'left' | 'right';        // position of toggle icon
    removeborder?: boolean;
    fontSize?: string;
    fontWeight?: number;
    menuFontWeight?: number;
    buttonTextColor?: string;
    buttonBackgroundColor?: string;
    buttonHoverColor?: string;
    buttonStyle?: React.CSSProperties;
    menuItemHoverColor?: string;
    menuItemSelectedBackgroundColor?: string;
    menuItemSelectedBorderColor?: string;
    menuItemSelectedTextColor?: string;
    menuItemSelectedIconColor?: string;
    showId?: boolean;
    width?: string;
    height?: string;


};

const Wrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const Menu = styled.div<{
    $position: 'top' | 'bottom';
    $align: 'left' | 'right';
    theme: Theme;
    removeborder?: boolean;
    $textColor?: string;
    width?: string;
    height?: string
}>`
  position: absolute;
  ${(props) => (props.$position === 'bottom' ? 'top: 100%;' : 'bottom: 100%;')}
  ${(props) => (props.$align === 'right' ? 'right: 0;' : 'left: 0;')}
  z-index: 1000;
  display: block;
  width: ${(props) => props.width || 'max-content'};
  max-height: ${(props) => props.height || 'auto'};
  max-width: 200px;
  padding: 8px 0px;
  font-size: 1rem;
  font-weight: 500;
  font-family: var(--font-raleway);
  max-width: 210px;
  overflow-y: auto;
  
    color: ${({ theme, $textColor }) => $textColor || theme.CorporateTheme.colors.textSecondary};
    background-color: ${({ theme }) => theme.CorporateTheme.colors.background};
  border: ${({ removeborder }) => removeborder ? 'none' : `1px solid rgba(0, 0, 0, 0.15)`};
  border-radius: 0.25rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  margin-top:10px;
`;

const MenuItem = styled.button<{ theme: Theme, menuFontWeight?: number; $hoverColor?: string; $selected?: boolean; $selectedBackgroundColor?: string; $selectedBorderColor?: string; $selectedTextColor?: string; $selectedIconColor?: string }>`
  display: flex;
align-items: center;
  width: 100%;
  font-weight: ${({ menuFontWeight }) => menuFontWeight || '400'};
  gap: 8px;
    color:  ${({ theme, $selected, $selectedTextColor }) => $selected ? ($selectedTextColor || theme.CorporateTheme.colors.primary) : theme.CorporateTheme.colors.textSecondary};
  padding:8px 15px;
  font-size: 14px;
  text-align: left;
  background-color: transparent;
  border: 0;
    border-left: ${({ $selected, $selectedBorderColor }) => $selected ? `3px solid ${$selectedBorderColor || '#1e3a8a'}` : '3px solid transparent'};
    background-color: ${({ $selected, $selectedBackgroundColor }) => $selected ? ($selectedBackgroundColor || 'rgba(30, 58, 138, 0.08)') : 'transparent'};

  &:hover {
        background-color: ${({ theme, $hoverColor }) => $hoverColor || theme.CorporateTheme.colors.headerBG};
  }
    &.icon-right {
    flex-direction: row-reverse;
  }
`;

const DropdownButton: React.FC<DropdownButtonProps> = ({
    buttonLabel,
    items,
    position,
    align = 'left',
    icon,
    iconPosition,
    removeborder,
    fontSize,
    fontWeight,
    menuFontWeight,
    buttonTextColor,
    buttonBackgroundColor,
    buttonHoverColor,
    buttonStyle,
    menuItemHoverColor,
    menuItemSelectedBackgroundColor,
    menuItemSelectedBorderColor,
    menuItemSelectedTextColor,
    menuItemSelectedIconColor,
    showId = false,
    width,
    height
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [autoPosition, setAutoPosition] = useState<'top' | 'bottom'>('bottom');
    const wrapperRef = useRef<HTMLDivElement>(null);
    const { theme } = useTheme();
    const toggleDropdown = () => {
        if (!position && wrapperRef.current) {
            const rect = wrapperRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;

            if (spaceBelow < 200 && spaceAbove > spaceBelow) {
                setAutoPosition('top');
            } else {
                setAutoPosition('bottom');
            }
        }

        setIsOpen((prev) => !prev);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const finalPosition = position ?? autoPosition;

    return (
        <Wrapper ref={wrapperRef}>
            <ShortcutButton
                icon={icon}
                label={buttonLabel}
                onClick={toggleDropdown}
                iconPostions={iconPosition}
                fontSize={fontSize}
                fontWeight={fontWeight}
                textColor={buttonTextColor}
                backgroundColor={buttonBackgroundColor}
                hoverBackgroundColor={buttonHoverColor}
                style={buttonStyle}
            />
            {isOpen && (
                <Menu width={width} height={height} $position={finalPosition} $align={align} theme={theme} removeborder={removeborder} $textColor={buttonTextColor}>
                    {items.map((item, index) => (
                        <MenuItem
                            key={index}
                            className={item.iconPosition === 'right' ? 'icon-right' : ''}
                            onClick={() => {
                                item.onClick();
                                setIsOpen(false);
                            }}
                            theme={theme}
                            menuFontWeight={menuFontWeight}
                            $hoverColor={menuItemHoverColor}
                            $selected={item.selected}
                            $selectedBackgroundColor={menuItemSelectedBackgroundColor}
                            $selectedBorderColor={menuItemSelectedBorderColor}
                            $selectedTextColor={menuItemSelectedTextColor}
                            $selectedIconColor={menuItemSelectedIconColor}
                        >
                            {item.icon && <span style={item.selected ? { color: menuItemSelectedIconColor || menuItemSelectedTextColor || theme.CorporateTheme.colors.primary } : undefined}>{item.icon}</span>}
                            <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.2 }}>
                                <span>{item.label}</span>
                                {showId && item.id !== undefined && item.id !== null && item.id !== '' ? (
                                    <span style={{ fontSize: '12px', fontWeight: 400, opacity: 0.75, marginTop: 2, color: '--var(--app-text-color)' }}>
                                        Id:{item.id}
                                    </span>
                                ) : null}
                            </span>
                        </MenuItem>
                    ))}
                </Menu>
            )}
        </Wrapper>
    );
};

export default DropdownButton;
